// Display values for the current association, derived from its `hoas` row.
//
// Shared screens must not hard-code one association's facts (CLAUDE.md rule 9).
// Columns hold the generic facts (name, legal_name, city, county, state,
// fiscal_year_start_month, support_email); `settings` (jsonb) holds the optional,
// association-specific extras. A missing extra hides the panel that needs it.
//
// settings keys read here (all optional):
//   location                 "Bigfork, Montana"
//   dues_label               "$400 per unit ($450 from Jan 2027)"
//   phases_label             "Phase 1 (No. 1) & Phase 2 (No. 2)"
//   entity_type              "Nonprofit Corporation (Title 35, Ch. 2, MCA)"
//   tax_status               "IRC §528 HOA Election (pending confirmation)"
//   meeting_location         "Bigfork Community Center"
//   meeting_agenda           ["Call to Order", ...]
//   reserves                 { total_label, sub_label, note, cd_ladder: [{institution, term, amount, maturity, rate}] }
//   dues_revenue             { value_label, unit_dues_label }
//   featured_project         { label, value_label, sub_label }
//   open_motion              { title, body }  board voting card
//   total_units_label        "107"  (else hoas.total_units)
//   reserves.short_label     "$135K" dashboard tile
//   priority_items           [{ title, detail, severity: 'high'|'medium' }]  board urgent items
//   status_attention         ["Legal Billing", "Member Roster"]  admin system-status rows
//   board_sender_label       "From: Philippe Villard"  member inbox, board-sent threads
//   management_company       { name, email, website }
//   map_query                "Crestview Drive Bigfork MT 59911"
//   landing_tagline          "Your community portal for Bigfork, Montana — ..."
//   email_header_url         invite / notification email hero image

const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
  CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho',
  IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina',
  ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas',
  UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia',
  WI: 'Wisconsin', WY: 'Wyoming',
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'];

export const ATRIONOS_SUPPORT_EMAIL = 'support@atrionos.com';

// "Jul 2026 – Jun 2027": the fiscal year containing `today`.
export function currentFiscalYearLabel(startMonth, today = new Date()) {
  const start = Number(startMonth);
  if (!Number.isInteger(start) || start < 1 || start > 12) return null;
  const startYear = today.getMonth() + 1 >= start ? today.getFullYear() : today.getFullYear() - 1;
  const endMonthIndex = (start + 10) % 12;
  const endYear = start === 1 ? startYear : startYear + 1;
  const short = (i) => MONTHS[i].slice(0, 3);
  return `${short(start - 1)} ${startYear} – ${short(endMonthIndex)} ${endYear}`;
}

function fiscalYearLabel(startMonth) {
  const start = Number(startMonth);
  if (!Number.isInteger(start) || start < 1 || start > 12) return null;
  const endIndex = (start + 10) % 12; // the month before the start month
  const lastDay = new Date(2001, endIndex + 1, 0).getDate();
  return `${MONTHS[start - 1]} 1 – ${MONTHS[endIndex]} ${lastDay}`;
}

export function hoaProfile(hoa) {
  const settings = hoa?.settings || {};
  const stateName = STATE_NAMES[String(hoa?.state || '').toUpperCase()] || hoa?.state || '';
  const cityState = [hoa?.city, stateName].filter(Boolean).join(', ');
  const location = settings.location || cityState || null;
  const locationLong = [cityState, hoa?.county ? `${hoa.county} County` : null].filter(Boolean).join(' — ') || location;
  const name = hoa?.name || 'Your Community';
  const shortName = name.replace(/\s+(HOA|Homeowners Association|Owners Association|Association)(,? Inc\.?)?$/i, '').trim() || name;

  return {
    name,
    shortName,
    legalName: hoa?.legal_name || name,
    location,
    locationLong,
    supportEmail: hoa?.support_email || ATRIONOS_SUPPORT_EMAIL,
    fiscalYear: fiscalYearLabel(hoa?.fiscal_year_start_month),
    duesLabel: settings.dues_label || null,
    phasesLabel: settings.phases_label || null,
    entityType: settings.entity_type || null,
    taxStatus: settings.tax_status || null,
    meetingLocation: settings.meeting_location || null,
    meetingAgenda: Array.isArray(settings.meeting_agenda) ? settings.meeting_agenda : null,
    reserves: settings.reserves || null,
    duesRevenue: settings.dues_revenue || null,
    featuredProject: settings.featured_project || null,
    openMotion: settings.open_motion || null,
    totalUnitsLabel: settings.total_units_label || (hoa?.total_units ? String(hoa.total_units) : null),
    reserveShortLabel: settings.reserves?.short_label || null,
    priorityItems: Array.isArray(settings.priority_items) ? settings.priority_items : [],
    statusAttention: Array.isArray(settings.status_attention) ? settings.status_attention : [],
    boardSenderLabel: settings.board_sender_label || 'From: The Board',
    currentFiscalYear: currentFiscalYearLabel(hoa?.fiscal_year_start_month),
    managementCompany: settings.management_company || null,
    mapQuery: settings.map_query || [hoa?.city, hoa?.state, hoa?.zip].filter(Boolean).join(' ') || null,
    landingTagline: settings.landing_tagline
      || (location ? `Your community portal for ${location}.` : 'Your community portal.'),
  };
}

// The rows of the admin "System Settings" panel; rows without a value are left out.
export function hoaSettingsRows(profile) {
  return [
    { label: 'HOA Name', value: profile.legalName },
    { label: 'Location', value: profile.locationLong },
    { label: 'Fiscal Year', value: profile.fiscalYear },
    { label: 'Annual Dues', value: profile.duesLabel },
    { label: 'Phases', value: profile.phasesLabel },
    { label: 'Entity Type', value: profile.entityType },
    { label: 'Tax Status', value: profile.taxStatus },
  ].filter((row) => row.value);
}
